import 'swiper/css';
import 'swiper/css/zoom';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import React, { Dispatch, SetStateAction } from 'react';
import { Keyboard, Navigation, Pagination, Zoom } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Icons from '@/core/components/Icons';
import Photo, { Item } from '@/core/components/Photo';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

interface SwiperCarouselImageProps {
  onClose: () => void;
  setActiveIndex: Dispatch<SetStateAction<number>>;
  activeIndex: number;
  images: Item[];
}

const SwiperCarouselImage: React.FC<SwiperCarouselImageProps> = ({ activeIndex, images, onClose, setActiveIndex }) => {
  const { t } = useTranslationContext(['common']);

  return (
    <div className="relative">
      <Swiper
        initialSlide={activeIndex}
        onActiveIndexChange={({ activeIndex }) => {
          setActiveIndex(activeIndex);
        }}
        keyboard
        className="h-screen"
        zoom
        navigation={{ prevEl: '.prevEl', nextEl: '.nextEl' }}
        pagination={{
          type: 'fraction',
          el: '.donnonsPagination',
        }}
        modules={[Zoom, Navigation, Pagination, Keyboard]}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('close', { ns: 'common' })}
          className="absolute right-5 top-10 z-50 flex size-8 shrink-0 items-center justify-center rounded-full border bg-content-button-secondary-disable text-content-button-secondary-disable hover:bg-bg-button-ghost-hover"
        >
          <Icons icon="close" size="24" />
        </button>
        {images.map(({ src, alt }, index) => (
          <SwiperSlide key={src}>
            <div className="swiper-zoom-container">
              <Photo index={index} item={{ src, alt }} className="max-h-screen overflow-hidden" />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="absolute left-6 top-1/2 z-50 hidden md:block">
        <button className="prevEl group size-10 rounded-full bg-[white] p-2 disabled:opacity-50 disabled:hover:bg-[white]">
          <Icons icon="chevron-left" className="group-disabled:group-hover:stroke-content-primary" />
        </button>
      </div>
      <div className="absolute right-6 top-1/2 z-50 hidden md:block">
        <button className="nextEl group size-10 rounded-full bg-[white] p-2 disabled:opacity-50 disabled:hover:bg-[white]">
          <Icons icon="chevron-right" className="group-disabled:group-hover:stroke-content-primary" />
        </button>
      </div>
      <div
        className="pagination absolute bottom-10 right-5 z-50 flex justify-center rounded-1 bg-[white] p-2 text-bg-dark"
        aria-label={t('components.carousel-images.full-screen-button.label', { ns: 'common' })}
      >
        <Icons icon="camera" color="primary" />
        <span className="ml-1">
          {activeIndex + 1} / {images.length}
        </span>
      </div>
    </div>
  );
};

export default SwiperCarouselImage;
